// src/App.js - routing, auth state, theme, and the signed-in app shell
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Analytics } from '@vercel/analytics/react';
import PortfolioNote from './components/PortfolioNote';
import ChatPage from './components/ChatPage';
import SettingsPage from './components/SettingsPage';
import Login from './components/Login';
import Premium from './components/Premium';
import PremiumSuccess from './components/PremiumSuccess';
import AppShell from './components/layout/AppShell';
import { CurrencyProvider } from './contexts/CurrencyContext';
import { ThemeProvider } from './contexts/ThemeContext';
import api, { isAuthenticated } from './api';
import logger from './utils/logger';

function App() {
  const [user, setUser] = useState(null);

  // Store user in localStorage for Premium component access
  const handleLogin = (userData) => {
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  // Handle logout - clear user state and localStorage
  const handleLogout = () => {
    setUser(null);
    localStorage.removeItem('user');
    localStorage.removeItem('jwt_token');
    logger.log('User logged out successfully');
  };

  // Restore user from localStorage on app load (only if JWT is still valid)
  useEffect(() => {
    const savedUser = localStorage.getItem('user');
    if (savedUser) {
      try {
        const userData = JSON.parse(savedUser);
        if (isAuthenticated()) {
          setUser(userData);
        } else {
          logger.warn('Session expired. Please sign in again.');
          localStorage.removeItem('user');
          localStorage.removeItem('jwt_token');
        }
      } catch (error) {
        console.error('Error parsing saved user data:', error);
        localStorage.removeItem('user');
      }
    }
  }, []);

  // Keep the health check for internal monitoring but don't display it
  useEffect(() => {
    api.get('/health')
      .then(response => {
        logger.log('Backend status:', response.data.status);
      })
      .catch(error => {
        console.error('Error connecting to backend:', error);
      });
  }, []);

  // Check for new app version — force reload if stale cache
  useEffect(() => {
    fetch(`/version.json?t=${Date.now()}`)
      .then(r => r.json())
      .then(data => {
        const stored = sessionStorage.getItem('app_version');
        if (stored && stored !== String(data.v)) {
          logger.log('New version detected, reloading...');
          sessionStorage.setItem('app_version', String(data.v));
          window.location.reload();
        } else {
          sessionStorage.setItem('app_version', String(data.v));
        }
      })
      .catch(() => {}); // Silently ignore if version.json missing
  }, []);

  return (
    <>
      <ThemeProvider>
        <CurrencyProvider>
          <Router>
            <Routes>
              {/* Public pages */}
              <Route path="/premium" element={<Premium />} />
              <Route path="/premium-success" element={<PremiumSuccess />} />

              {user ? (
                <Route element={<AppShell user={user} onLogout={handleLogout} />}>
                  <Route path="/" element={<Navigate to="/portfolio" replace />} />
                  <Route path="/portfolio" element={<PortfolioNote user={user} onLogout={handleLogout} />} />
                  <Route path="/chat" element={<ChatPage user={user} />} />
                  <Route path="/settings" element={<SettingsPage user={user} />} />
                  <Route path="*" element={<Navigate to="/portfolio" replace />} />
                </Route>
              ) : (
                // Any app URL shows the landing page; after sign-in the same URL renders inside the shell.
                <Route path="*" element={<Login onLogin={handleLogin} />} />
              )}
            </Routes>
          </Router>
        </CurrencyProvider>
      </ThemeProvider>
      <Analytics />
    </>
  );
}

export default App;
